import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import i18n, { SUPPORTED_LANGUAGES } from '@/i18n';
import { setBiggerFontSize, setContrastColor } from '@/lib/accessibility';

const API = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

// Server preferences mají precedenci nad localStorage — když se uživatel
// přihlásí na novém zařízení, jeho profil se aplikuje rovnou.
function applyUserPreferences(user) {
  if (!user) return;
  if (user.language && SUPPORTED_LANGUAGES.includes(user.language) && i18n.language !== user.language) {
    i18n.changeLanguage(user.language);
  }
  if (typeof user.accessibilityColorContrast === 'boolean') {
    setContrastColor(user.accessibilityColorContrast);
  }
  if (typeof user.accessibilityFontSize === 'boolean') {
    setBiggerFontSize(user.accessibilityFontSize);
  }
}

// Attach token to all requests
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('ct-token');
  if (token && !config.headers['Authorization']) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('ct-token');
      if (!token) {
        setUser(false);
        setLoading(false);
        return;
      }
      const { data } = await axios.get(`${API}/api/auth/me`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      applyUserPreferences(data);
      setUser(data);
    } catch {
      localStorage.removeItem('ct-token');
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (username, password) => {
    const { data } = await axios.post(`${API}/api/auth/login`, { username, password }, { withCredentials: true });
    if (data.accessToken) {
      localStorage.setItem('ct-token', data.accessToken);
    }
    applyUserPreferences(data);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } catch {}
    localStorage.removeItem('ct-token');
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
